import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getOneStory, updateStory } from 'slice/storySlice';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const Update = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { single: data, loading } = useSelector((state) => state.story || {});

  const [formData, setFormData] = useState({
    heading: '',
    category: '',
    image: null,
    existingImage: '',
    existingExtras: [],
    extraItems: []
  });

  useEffect(() => {
    dispatch(getOneStory(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      setFormData({
        heading: data.heading || '',
        category: data.category || '',
        image: null,
        existingImage: data.image ? `${process.env.REACT_APP_BACKEND}uploads/stories/${data.image}` : '',
        existingExtras:
          Array.isArray(data.images) && Array.isArray(data.image_headings)
            ? data.images.map((img, i) => ({
                image: img,
                heading: data.image_headings[i] || ''
              }))
            : [],
        extraItems: []
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { files } = e.target;
    setFormData((prev) => ({ ...prev, image: files[0] }));
  };

  const addExtraItem = () => {
    setFormData((prev) => ({
      ...prev,
      extraItems: [...prev.extraItems, { image: null, heading: '' }]
    }));
  };

  const removeExtraItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      extraItems: prev.extraItems.filter((_, i) => i !== index)
    }));
  };

  const handleExtraChange = (index, field, value) => {
    setFormData((prev) => {
      const next = [...prev.extraItems];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, extraItems: next };
    });
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const updatedData = new FormData();
    updatedData.append('heading', formData.heading);
    updatedData.append('category', formData.category);
    if (formData.image) updatedData.append('image', formData.image);

    try {
      if (formData.extraItems.length > 0) {
        const images = await Promise.all(
          formData.extraItems.map(async (item) => (item.image ? await fileToBase64(item.image) : null))
        );
        const imageHeadings = formData.extraItems.map((item) => item.heading || '');
        updatedData.append('images', JSON.stringify(images));
        updatedData.append('image_headings', JSON.stringify(imageHeadings));
      }

      dispatch(updateStory({ id, data: updatedData }));
      toast.success('Story updated successfully!');
      navigate('/story');
    } catch (error) {
      console.error('There was a problem updating the story:', error.message);
    }
  };

  return (
    <div className="container card my-5">
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-sm-6 mt-2">
            <label htmlFor="heading">Heading</label>
            <input
              type="text"
              id="heading"
              name="heading"
              className="form-control"
              value={formData.heading}
              onChange={handleChange}
              required
            />
          </div>

          <div className="col-sm-6 mt-2">
            <label htmlFor="category">Category</label>
            <select id="category" name="category" className="form-control" value={formData.category} onChange={handleChange} required>
              <option value="" disabled>
                Select category
              </option>
              <option value="desh">Desh</option>
              <option value="videsh">Videsh</option>
              <option value="sports">Sports</option>
              <option value="dharm">Dharm</option>
              <option value="cinema">Cinema</option>
            </select>
          </div>

          <div className="col-sm-6 mt-2">
            <label htmlFor="image">Image</label>
            <input type="file" id="image" name="image" className="form-control" accept="image/*" onChange={handleFileChange} />
          </div>
        </div>

        <div className="mt-3">
          {formData.image ? (
            <img src={URL.createObjectURL(formData.image)} width={120} alt="preview" />
          ) : (
            formData.existingImage && <img src={formData.existingImage} width={120} alt="existing" />
          )}
        </div>

        {formData.existingExtras.length > 0 && (
          <div className="mt-4">
            <h6>Existing Extra Images</h6>
            <div className="row">
              {formData.existingExtras.map((item, index) => (
                <div className="col-sm-4 mb-3" key={`existing-${index}`}>
                  <img src={`${process.env.REACT_APP_BACKEND}uploads/stories/${item.image}`} width={120} alt={item.heading || 'extra'} />
                  <div>{item.heading}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center">
            <h6>Replace Extra Images</h6>
            <button type="button" className="btn btn-sm btn-secondary" onClick={addExtraItem}>
              Add Image
            </button>
          </div>
          {formData.extraItems.map((item, index) => (
            <div className="row mt-3" key={`extra-${index}`}>
              <div className="col-sm-6">
                <label htmlFor={`extraImage-${index}`}>Extra Image</label>
                <input
                  type="file"
                  id={`extraImage-${index}`}
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => handleExtraChange(index, 'image', e.target.files[0])}
                  required
                />
              </div>
              <div className="col-sm-5">
                <label htmlFor={`extraHeading-${index}`}>Image Heading</label>
                <input
                  type="text"
                  id={`extraHeading-${index}`}
                  className="form-control"
                  value={item.heading}
                  onChange={(e) => handleExtraChange(index, 'heading', e.target.value)}
                  required
                />
              </div>
              <div className="col-sm-1 d-flex align-items-end">
                <button type="button" className="btn btn-danger btn-sm" onClick={() => removeExtraItem(index)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 text-right">
          <button className="btn btn-success" disabled={loading}>
            Update Story
          </button>
        </div>
      </form>
    </div>
  );
};

export default Update;
